import React, { FC } from 'react';
import { DialogContent, Grid, TextField, DialogActions, Button, FormControl } from '@mui/material';
import { useMutation } from '@apollo/client';
import { modifyCategoryTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

type EditCategoryFormProps = {
    onCloseDialog: () => void;
    categoryToEdit: { _id: string; name: string };
    onEditCategory: (category: { _id: string; name: string }) => void;
};
const EditCategoryForm: FC<EditCategoryFormProps> = ({
    onCloseDialog,
    categoryToEdit,
    onEditCategory,
}: EditCategoryFormProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'workflow']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyCategory] = useMutation(modifyCategoryTemplate, {
        onCompleted: ({ modifyCategory }) => {
            onEditCategory(modifyCategory);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: { name: string }) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('workflow:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            ...categoryToEdit,
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            await modifyCategory({ variables: { ...values } });
            onCloseDialog();
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                id="category-name"
                                margin="dense"
                                label={translate('actions-materials:CATEGORY_NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button variant="contained" disabled={!formik.isValid} type="submit">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default EditCategoryForm;
